import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import serviceReportsApi, { CompanyServiceReport } from "../api/serviceReportsApi";
import { ServiceRequest, ServiceRequestStatusCode } from "../../servicing/models/models";
import { WorkOrder } from "../models/models";

// ─── Types ────────────────────────────────────────────────────

export type SrTab = "untriaged" | "internal" | "external" | "resolved";

/** An SR enriched with its linked WO or RFS context. */
export interface EnrichedServiceReport {
  sr: CompanyServiceReport;
  tab: SrTab;
  /** Linked WO (Internal tab). */
  linkedWo?: WorkOrder;
  /** Linked RFS (External tab). */
  linkedRfs?: ServiceRequest;
  /** True if linked RFS is a draft (statusCode === 1). */
  isRfsDraft?: boolean;
}

export interface ServiceReportsPageState {
  serviceReports: CompanyServiceReport[];
  workOrders: WorkOrder[];
  serviceRequests: ServiceRequest[];
  enriched: EnrichedServiceReport[];
  loading: boolean;
  error: string | null;
}

// ─── Thunks ───────────────────────────────────────────────────

export const fetchServiceReportsData = createAsyncThunk(
  "serviceReportsPage/fetchAll",
  async () => {
    // Parallel fetch from 3 microservices
    const [srs, wos, rfses] = await Promise.all([
      serviceReportsApi.getCompanyServiceReports(),
      serviceReportsApi.getAllWorkOrders(),
      serviceReportsApi.getAllServiceRequests(),
    ]);
    return { srs, wos, rfses };
  }
);

// ─── Client-side join logic ───────────────────────────────────

function enrichReports(
  srs: CompanyServiceReport[],
  wos: WorkOrder[],
  rfses: ServiceRequest[]
): EnrichedServiceReport[] {
  // Defensive: ensure all inputs are arrays
  const safeSrs = Array.isArray(srs) ? srs : [];
  const safeWos = Array.isArray(wos) ? wos : [];
  const safeRfses = Array.isArray(rfses) ? rfses : [];

  // Build lookup maps for efficient joining
  // WOs: join by linkedServiceReports array (multi-SR-per-WO junction)
  // Falls back to old serviceReportId field during transition
  const woByReportId = new Map<string, WorkOrder>();
  for (const wo of safeWos) {
    // New: iterate linked service reports array
    if (wo.linkedServiceReports && wo.linkedServiceReports.length > 0) {
      for (const link of wo.linkedServiceReports) {
        woByReportId.set(link.serviceReportId.toLowerCase(), wo);
      }
    }
    // Backward compat: fall back to old field during transition
    else if (wo.serviceReportId) {
      woByReportId.set(wo.serviceReportId.toLowerCase(), wo);
    }
  }

  // RFSes: join by serviceReportNumber matching SR.number
  const rfsByReportNumber = new Map<string, ServiceRequest>();
  for (const rfs of safeRfses) {
    if (rfs.serviceReportNumber) {
      rfsByReportNumber.set(rfs.serviceReportNumber.toLowerCase(), rfs);
    }
  }

  return safeSrs.map((sr) => {
    const srIdLower = sr.id?.toLowerCase() ?? "";
    const srNumberLower = sr.number?.toLowerCase() ?? "";

    // Check if resolved first (statusCode === 2 = Solved)
    if (sr.statusCode === 2) {
      // Even for resolved, attach linked WO/RFS for display
      const linkedWo = woByReportId.get(srIdLower) || (srNumberLower ? woByReportId.get(srNumberLower) : undefined);
      const linkedRfs = rfsByReportNumber.get(srNumberLower);
      return { sr, tab: "resolved" as SrTab, linkedWo, linkedRfs };
    }

    // Check for linked WO (Internal) — try matching by SR id first, then by SR number
    const linkedWo = woByReportId.get(srIdLower) || (srNumberLower ? woByReportId.get(srNumberLower) : undefined);
    if (linkedWo) {
      return { sr, tab: "internal" as SrTab, linkedWo };
    }

    // Check for linked RFS (External)
    const linkedRfs = rfsByReportNumber.get(srNumberLower);
    if (linkedRfs) {
      const isRfsDraft = linkedRfs.statusCode === ServiceRequestStatusCode.draft;
      // Draft RFS: keep in Untriaged with indicator
      if (isRfsDraft) {
        return { sr, tab: "untriaged" as SrTab, linkedRfs, isRfsDraft: true };
      }
      // Published RFS: External tab
      return { sr, tab: "external" as SrTab, linkedRfs };
    }

    // No link: Untriaged
    return { sr, tab: "untriaged" as SrTab };
  });
}

// ─── Initial state ────────────────────────────────────────────

const initialState: ServiceReportsPageState = {
  serviceReports: [],
  workOrders: [],
  serviceRequests: [],
  enriched: [],
  loading: false,
  error: null,
};

// ─── Slice ────────────────────────────────────────────────────

const serviceReportsPageSlice = createSlice({
  name: "serviceReportsPage",
  initialState,
  reducers: {
    clearServiceReportsPage: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceReportsData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceReportsData.fulfilled, (state, { payload }) => {
        state.serviceReports = payload.srs;
        state.workOrders = payload.wos;
        state.serviceRequests = payload.rfses;
        state.enriched = enrichReports(payload.srs, payload.wos, payload.rfses);
        state.loading = false;
      })
      .addCase(fetchServiceReportsData.rejected, (state, { error }) => {
        state.error = error.message || "Failed to load service reports data";
        state.loading = false;
      });
  },
});

export const { clearServiceReportsPage } = serviceReportsPageSlice.actions;
export default serviceReportsPageSlice.reducer;
